import { useEffect, useMemo, useRef, useState } from 'react';
import { SubscriptionApiType } from '../types/api/SubscriptionApiType';
import { Products } from '../api/graphql/products/types';
import { useMasterContext } from '../context';
import { getAllProductIds, getCategoriesProductIds } from '../utils/subscriptionDetail/productsIds';
import { fetchGraphqlProducts } from '../api/graphql/products';
import { satisfiedBoxSettings } from '../utils/boxSettings';
import { buildSubscriptionProducts } from '../utils/subscriptionDetail/subscriptionProducts';
import { ParentParams } from '../types/constant/PagesPropsType';
import { createCategoryContext } from '../utils/categories';
import { PlanGroupsStateType } from '../types/api/PlanGroupApiType';
import { getPlanGroups } from '../utils/getPlanGroups';
import { billingPolicyIntervals } from '../constants';

export const useSubscriptionProducts = (
  subscription: SubscriptionApiType | undefined,
  parentParams: ParentParams,
  token: string,
  reloadKey = 0
) => {
  /** ====== プラングループ取得 ====== */

  // プラングループ取得APIの結果を格納。キー=プラングループID、値=plansの配列（商品追加・変更時に使用）
  const [planGroups, setPlanGroups] = useState<PlanGroupsStateType>({});
  // 初回のみ getPlanGroups を呼ぶためのフラグ。
  // 取得可否は描画判定にも使うため state で保持し、依存計算に反映できるようにする。
  const [hasFetchedPlanGroups, setHasFetchedPlanGroups] = useState(false);

  // 詳細画面表示時、初回のみプラングループ取得APIを呼び planGroups に格納する。
  // reloadKey が増えたときは再取得を許可する。
  useEffect(() => {
    if (!token) return;
    if (hasFetchedPlanGroups) return;
    getPlanGroups(token)
      .then((data) => {
        setPlanGroups(data);
        setHasFetchedPlanGroups(true);
      })
      .catch(() => {
        setHasFetchedPlanGroups(false);
      });
  }, [token, reloadKey, hasFetchedPlanGroups]);

  /** ====== 商品情報取得 ====== */

  const { categories, boxSettings } = useMasterContext();

  const lines = subscription?.subscriptionLines;
  const totalOrder = subscription?.totalOrderCount ?? 0;
  const interval = subscription?.billingPolicyInterval;
  const intervalCount = subscription?.billingPolicyIntervalCount ?? 0;
  const customerTags = parentParams.customerTags;
  const totalSpent = parentParams.totalSpent;

  const [products, setProducts] = useState<Products | null>(null);
  /** 直近で GraphQL 取得に使った商品 ID 集合（内容が変わったときだけ再フェッチする） */
  const lastFetchedProductIdsKeyRef = useRef<string>('');

  // 手動リトライ時は取得キャッシュを破棄して、同じ内容でも再取得する。
  // ここで products/planGroups を null/空に戻し、画面側がローディング表示へ切り替わるようにする。
  useEffect(() => {
    setHasFetchedPlanGroups(false);
    lastFetchedProductIdsKeyRef.current = '';
    setPlanGroups({});
    setProducts(null);
  }, [reloadKey]);

  useEffect(() => {
    async function fetchProducts() {
      // 次回お届け内容を組み立てるために必要な前提データが揃うまで待つ。
      if (!categories || !boxSettings || !lines?.length) return;

      const allProductIds = getAllProductIds(lines, categories, boxSettings);
      if (allProductIds.size === 0) return;

      // 商品ID集合が同じなら再取得しない（無駄な GraphQL 呼び出しを防止）。
      const idsFetchKey = Array.from(allProductIds).sort().join('\0');
      if (idsFetchKey === lastFetchedProductIdsKeyRef.current) return;

      const productsRes = await fetchGraphqlProducts(Array.from(allProductIds));
      lastFetchedProductIdsKeyRef.current = idsFetchKey;
      setProducts(productsRes);
    }
    fetchProducts();
  }, [categories, boxSettings, lines, reloadKey]);

  const subscriptionProducts = useMemo(() => {
    // どれか1つでも不足している間は null を返し、画面側でローディング/再読み込み導線を出す。
    if (!boxSettings || !categories || !products || !interval || !lines?.length) return null;
    if (!hasFetchedPlanGroups) return null;

    const categoryIds = getCategoriesProductIds(categories);
    const bss = satisfiedBoxSettings(lines, categoryIds, boxSettings);
    const ctx = createCategoryContext(lines, totalOrder, customerTags, totalSpent);

    const pickSub = {
      subscriptionLines: lines,
      totalOrderCount: totalOrder,
      billingPolicyInterval: interval,
      billingPolicyIntervalCount: intervalCount,
      // getNextOrderNumberForPricing 用。totalOrderCount だけだと回数がずれることがあるため両方渡す
      billingAttempts: subscription.billingAttempts,
    };
    return buildSubscriptionProducts(pickSub, products, planGroups, categories, bss, ctx);
  }, [
    boxSettings,
    categories,
    customerTags,
    interval,
    intervalCount,
    lines,
    hasFetchedPlanGroups,
    planGroups,
    products,
    totalOrder,
    totalSpent,
  ]);

  return {
    subscribedProducts: subscriptionProducts?.subscribedProducts ?? null,
    subscribableProducts: subscriptionProducts?.subscribableProducts ?? null,
    products,
    planGroups,
  };
};
